import * as UBUtils from './ub-utils.ts';

type UBCoreConfig = {
  page: string;
  category?: string | null;
  action?: string | null;
  label?: string | null;
  value?: string | object | null;
  device_category: string;
  user_agent: string;
  status_code: number;
  ubact: any;
  ubcl: string;
  ubses: string;
  ubref: string;
};

type MergeOptionalConfig = {
  action?: UBCoreConfig['action'];
  category?: UBCoreConfig['category'];
  label?: UBCoreConfig['label'];
  value?: UBCoreConfig['value'];
};

const UBCore = {
  account: UBUtils.account(),
  storagePrefix: 'ubtracking_',
  endpoint: '/ubtracking',
  abTestsPrefix: '',
  baseURL: 'https://ub.gutefrage.net/__ub.gif',
  state: {},
  pageViewed: false,

  UBUtils,

  setup: function (config: {
    abTestsPrefix: string;
    account?: string;
    storagePrefix?: string;
    endpoint?: string;
    state?: any;
  }): any {
    config.account && (UBCore.account = config.account);
    config.storagePrefix && (UBCore.storagePrefix = config.storagePrefix);
    config.endpoint && (UBCore.endpoint = config.endpoint);
    config.state && (UBCore.state = config.state);
    config.abTestsPrefix && (UBCore.abTestsPrefix = config.abTestsPrefix);

    return UBCore;
  },

  init: function (): void {
    UBCore.UBUtils.sessionId();
    UBCore.UBUtils.clientId();
  },

  merge(ubAction: any, { action, category, label, value }: MergeOptionalConfig): any {
    const config: UBCoreConfig = {
      page: UBCore.UBUtils.currentURL(),
      device_category: UBCore.UBUtils.deviceCategory(),
      user_agent: UBCore.UBUtils.userAgent(),
      status_code: UBCore.UBUtils.httpStatusCode(),
      ubact: ubAction,
      ubcl: UBCore.UBUtils.clientId(),
      ubses: UBCore.UBUtils.sessionId(),
      ubref: UBCore.UBUtils.referrer()
    };

    if (action) config.action = action;
    if (category) config.category = category;
    if (label) config.label = label;
    if (value) config.value = value;

    return config;
  },

  send: function (data: any): void {
    navigator.sendBeacon(UBCore.endpoint, JSON.stringify(data));
  },

  trackEvent: {
    click: function (
      category: string | null = null,
      label: string | null = null,
      value: string | object | null = null
    ): void {
      const data: any = UBCore.merge('trackEvent', { action: 'click', category, label, value });
      UBCore.send(data);
    },

    view: function (
      category: string | null = null,
      label: string | null = null,
      value: string | object | null = null
    ): void {
      const data: any = UBCore.merge('trackEvent', { action: 'view', category, label, value });
      UBCore.send(data);
    },

    attached: function (
      category: string | null = null,
      label: string | null = null,
      value: string | null = null
    ): void {
      const data: any = UBCore.merge('trackEvent', { action: 'attached', category, label, value });
      UBCore.send(data);
    },

    timeOnSite: function (value: string, category: string = 'time-on-site'): void {
      const data: any = UBCore.merge('trackEvent', { category, value });
      UBCore.send(data);
    }
  },

  trackPageView: function (skipViewedCheck: boolean = false): void {
    if (UBCore.pageViewed && !skipViewedCheck) {
      console.warn(
        'This method was already executed. If you want to track a page view again, please reload the page.'
      );
      return;
    }

    UBCore.pageViewed = true;

    const data: any = UBCore.merge('trackPageView', { action: 'view' });
    UBCore.send(data);
  },

  trackTarget: function (target: string | HTMLElement | null, trackEvent: any): void {
    if (!target) {
      console.error('The target element is not found.');
      return;
    }

    if (typeof target === 'string') {
      target = document.querySelector(target) as HTMLElement;
    }

    if (trackEvent.click) {
      target.addEventListener('click', function (): void {
        UBCore.trackEvent.click(
          trackEvent.click.category,
          trackEvent.click.label,
          trackEvent.click.value || null
        );
      });
    }

    if (trackEvent.view) {
      const observer = new IntersectionObserver(entries => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          UBCore.trackEvent.view(
            trackEvent.view.category,
            trackEvent.view.label,
            trackEvent.view.value || null
          );
        }
      });

      observer.observe(target);
    }

    return;
    // This part will be implemented later. First, we need to implement the click event.
    //
    // // TODO: Add the attached event handler.
    // if (trackEvent.attached) {
    //     UBCore.trackEvent.attached(trackEvent.attached.category, trackEvent.attached.label);
    // }
  },

  onTrackTargetClick: function (target: HTMLElement): void {
    const category: string | null = target.dataset.ubCategory || null;
    const label: string | null = target.dataset.ubLabel || null;
    const value: string | null = target.dataset.ubValue || null;

    UBCore.trackEvent.click(category, label, value);
  },

  onTrackTargetView: function (
    target: HTMLElement,
    category: string,
    label: string,
    value: string | object | null = null
  ): void {
    UBCore.trackTarget(target, {
      view: {
        category,
        label,
        value
      }
    });
  }
};

export default UBCore;
